/**
 * Render explicit physical print pages to PDF (jsPDF + html2canvas).
 * Multi-page orders are grouped by copy: complete order copy 1, then copy 2.
 */
import jsPDF from 'jspdf';
import { drawPdfCopyBadge } from './pdfCopyBadge';
import { CHEETAH_LZ17_PAPER } from './cheetahLz17Geometry';

export function groupPrintablePagesByOrder(pages) {
  const groups = [];
  (pages || []).forEach((page, index) => {
    const orderKey = String(page.orderKey || `page-${index}`);
    const previous = groups[groups.length - 1];
    if (previous && previous.orderKey === orderKey) {
      previous.pages.push(page);
    } else {
      groups.push({ orderKey, pages: [page] });
    }
  });
  return groups;
}

export function buildPdfPageSequence(groups, copiesPerOrder = 1, copiesByOrder = {}) {
  const defaultCopies = Math.max(1, parseInt(copiesPerOrder, 10) || 1);
  const sequence = [];
  (groups || []).forEach((group) => {
    const copies = Math.max(
      1,
      parseInt(copiesByOrder?.[group.orderKey], 10) || defaultCopies,
    );
    for (let copy = 1; copy <= copies; copy += 1) {
      group.pages.forEach((page) => {
        sequence.push({ ...page, copy, copies });
      });
    }
  });
  return sequence;
}

/** Count explicit physical pages per order from generated print HTML. */
export function countPhysicalPagesByOrderFromHtml(fullHtml) {
  const counts = {};
  const tags = String(fullHtml || '').match(/<div\b[^>]*>/gi) || [];
  tags.forEach((tag) => {
    if (
      !/\bclass="[^"]*\b(?:lz-sheet|packing-slip-page|go4-outside-page)\b[^"]*"/i.test(
        tag,
      )
    ) {
      return;
    }
    const orderMatch = tag.match(/\bdata-print-order="([^"]+)"/i);
    if (!orderMatch?.[1]) return;
    const orderKey = orderMatch[1];
    counts[orderKey] = (counts[orderKey] || 0) + 1;
  });
  return counts;
}

function discoverPrintableBlocks(host) {
  const selectors = [
    ['.lz-sheet', 'lz-sheet'],
    ['.packing-slip-page', 'packing-slip-page'],
    ['.main', 'main'],
    ['.container', 'container'],
  ];
  for (const [selector, mode] of selectors) {
    const blocks = Array.from(host.querySelectorAll(selector));
    if (blocks.length) return { blocks, mode };
  }
  return { blocks: [], mode: 'none' };
}

function assertNoRenderedOverflow(container, mode, orderKey) {
  if (mode !== 'lz-sheet') return;
  const guards = Array.from(container.querySelectorAll('.lz-stub'));
  guards.forEach((guard) => {
    if (guard.scrollHeight > guard.clientHeight + 2) {
      throw new Error(
        `Packing slip overflow detected for order ${orderKey}. ` +
          'The Cheetah stub exceeded its safe printable area.',
      );
    }
  });
}

export function getCheetahCopyBadgeBounds(pdfWidth, pdfHeight) {
  const paper = CHEETAH_LZ17_PAPER;
  return {
    left: (paper.label.leftMm / paper.pageWidthMm) * pdfWidth,
    top: (paper.label.topMm / paper.pageHeightMm) * pdfHeight,
    width: (paper.label.widthMm / paper.pageWidthMm) * pdfWidth,
    height: (paper.label.heightMm / paper.pageHeightMm) * pdfHeight,
  };
}

/**
 * @param {string} fullHtml
 * @param {{
 *   fileName?: string,
 *   pageFormat?: 'a4'|number[],
 *   orientation?: 'p'|'l',
 *   hostWidthPx?: number,
 *   copiesPerOrder?: number,
 *   copiesByOrder?: Record<string, number>,
 * }} [opts]
 */
export async function printHtmlDocumentToPdf(fullHtml, opts = {}) {
  const { default: html2canvas } = await import('html2canvas');
  let css = '';
  let body = fullHtml;
  try {
    const cssMatch = fullHtml.match(/<style>([\s\S]*?)<\/style>/i);
    if (cssMatch) css = cssMatch[1];
    const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) body = bodyMatch[1];
  } catch {
    /* use the original HTML */
  }

  const hostWidthPx = Number(opts.hostWidthPx) > 0 ? Number(opts.hostWidthPx) : 794;
  const copies = Math.max(1, parseInt(opts.copiesPerOrder, 10) || 1);
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-99999px;top:0;width:${hostWidthPx}px;background:#fff;`;
  host.innerHTML = `<style>${css}</style>${body}`;
  document.body.appendChild(host);

  const { blocks, mode } = discoverPrintableBlocks(host);
  if (!blocks.length) {
    document.body.removeChild(host);
    throw new Error(
      'No printable blocks found (.lz-sheet / .packing-slip-page / .main / .container)',
    );
  }

  const orientation = opts.orientation === 'l' ? 'l' : 'p';
  const format = opts.pageFormat || 'a4';
  const pdf = new jsPDF({ orientation, unit: 'pt', format });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const capturedPages = [];

  try {
    for (let index = 0; index < blocks.length; index += 1) {
      const block = blocks[index];
      const orderKey =
        block.getAttribute('data-print-order') ||
        block.querySelector('[data-print-order]')?.getAttribute('data-print-order') ||
        `page-${index}`;
      const container = document.createElement('div');
      container.style.cssText =
        `position:fixed;left:-99999px;top:0;width:${hostWidthPx}px;background:#fff;`;
      const wrap =
        mode === 'main'
          ? `<div class="print-root">${block.outerHTML}</div>`
          : block.outerHTML;
      container.innerHTML = `<style>${css}</style>${wrap}`;
      document.body.appendChild(container);

      try {
        const images = Array.from(container.querySelectorAll('img'));
        await Promise.all(
          images.map(
            (image) =>
              new Promise((resolve) => {
                if (image.complete) return resolve();
                image.onload = image.onerror = () => resolve();
              }),
          ),
        );
        assertNoRenderedOverflow(container, mode, orderKey);
        const canvas = await html2canvas(container, {
          useCORS: true,
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: hostWidthPx,
          width: hostWidthPx,
        });
        const fitHeight = (canvas.height * pdfWidth) / canvas.width;
        if (mode === 'packing-slip-page' && fitHeight > pdfHeight + 2) {
          throw new Error(
            `Packing slip overflow detected for order ${orderKey}. ` +
              'A standard continuation page exceeded the physical sheet.',
          );
        }
        capturedPages.push({
          orderKey,
          imageData: canvas.toDataURL('image/png'),
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
          mode,
        });
      } finally {
        document.body.removeChild(container);
      }
    }
  } finally {
    document.body.removeChild(host);
  }

  const groups = groupPrintablePagesByOrder(capturedPages);
  const sequence = buildPdfPageSequence(groups, copies, opts.copiesByOrder);
  sequence.forEach((page, index) => {
    if (index > 0) pdf.addPage();
    const fitHeight = (page.canvasHeight * pdfWidth) / page.canvasWidth;
    if (page.mode === 'lz-sheet' && fitHeight > pdfHeight) {
      const width = (page.canvasWidth * pdfHeight) / page.canvasHeight;
      pdf.addImage(
        page.imageData,
        'PNG',
        (pdfWidth - width) / 2,
        0,
        width,
        pdfHeight,
        undefined,
        'FAST',
      );
    } else {
      pdf.addImage(
        page.imageData,
        'PNG',
        0,
        0,
        pdfWidth,
        fitHeight,
        undefined,
        'FAST',
      );
    }
    drawPdfCopyBadge(
      pdf,
      page.copy,
      page.copies,
      page.mode === 'lz-sheet'
        ? {
            bounds: getCheetahCopyBadgeBounds(pdfWidth, pdfHeight),
            inset: (4 / CHEETAH_LZ17_PAPER.pageWidthMm) * pdfWidth,
            placement: 'bottom-center',
          }
        : undefined,
    );
  });

  const fileName =
    opts.fileName || `packing-slips-${new Date().toISOString().substring(0, 10)}.pdf`;
  pdf.save(fileName);
  return {
    pages: sequence.length,
    physicalPagesPerCopy: capturedPages.length,
    mode,
    copies,
  };
}
